"use client";

import { createContext, useContext, useEffect, useState, ReactNode } from "react";
import { User } from "@/app/dashboard/orders-to-store-old/form";
import axios from "axios";

interface AuthContextType {
  user: User | null;
  isLoading: boolean;
  isLoggingOut: boolean;
  login: (userData: User, token: string) => void;
  logout: () => void;
  checkAuth: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoggingOut, setIsLoggingOut] = useState(false);

  const login = (userData: User, token: string) => {
    setUser(userData);
    sessionStorage.setItem("user", JSON.stringify(userData));
    localStorage.setItem("token", token);
  };

  const logout = async () => {
    try {
      setIsLoggingOut(true);
      
      // Clear storage first
      sessionStorage.removeItem("user");
      localStorage.removeItem("token");
      
      // Signal logout ke tab lain menggunakan localStorage event
      localStorage.setItem("logout-signal", Date.now().toString());
      setTimeout(() => {
        localStorage.removeItem("logout-signal");
      }, 100);
      
      // Call logout API to clear server-side session/cookie
      try {
        await axios.post("/api/logout");
      } catch (error) {
        console.error("Logout API call failed:", error);
        // Continue with logout even if API call fails
      }
      
      // Set user null setelah cleanup
      setUser(null);
      
    } catch (error) {
      console.error("Logout error:", error);
      // Force clear everything even if there's an error
      setUser(null);
      sessionStorage.removeItem("user");
      localStorage.removeItem("token");
    } finally {
      // Reset logging out flag
      setTimeout(() => {
        setIsLoggingOut(false);
      }, 300);
    }
  };

  const checkAuth = async () => {
    try {
      setIsLoading(true);
      
      // Jangan cek auth jika sedang dalam proses logout
      if (isLoggingOut) {
        setIsLoading(false);
        return;
      }
      
      // Cek sessionStorage dulu untuk performa
      const userString = sessionStorage.getItem("user");
      if (userString) {
        const userData = JSON.parse(userString);
        setUser(userData);
        setIsLoading(false);
        return;
      }

      // Jika tidak ada di sessionStorage, coba ambil dari server menggunakan cookie atau localStorage token
      try {
        const response = await axios.get("/api/auth/me");
        
        if (response.data.user) {
          const userData = response.data.user;
          setUser(userData);
          sessionStorage.setItem("user", JSON.stringify(userData));
        }
      } catch (apiError) {
        // Jika API call gagal, coba dengan token dari localStorage untuk verify
        const token = localStorage.getItem("token");
        if (token) {
          try {
            const response = await fetch("/api/verify-token", {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${token}`,
              },
            });
            
            if (response.ok) {
              const result = await response.json();
              if (result.user) {
                const userData = result.user;
                setUser(userData);
                sessionStorage.setItem("user", JSON.stringify(userData));
              }
            } else {
              // Token tidak valid, clear storage
              localStorage.removeItem("token");
              sessionStorage.removeItem("user");
            }
          } catch (verifyError) {
            console.error("Token verification failed:", verifyError);
            // Clear invalid token
            localStorage.removeItem("token");
            sessionStorage.removeItem("user");
          }
        }
      }
    } catch (error) {
      console.error("Auth check failed:", error);
      // Jika gagal dan tidak sedang logout, clear semua data auth
      if (!isLoggingOut) {
        setUser(null);
        sessionStorage.removeItem("user");
        localStorage.removeItem("token");
      }
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (!isLoggingOut) {
      checkAuth();
    }
  }, [isLoggingOut]);

  // Listen untuk storage changes di tab lain
  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === "user") {
        if (e.newValue) {
          setUser(JSON.parse(e.newValue));
          setIsLoggingOut(false); // Reset logout state jika user login di tab lain
        } else {
          // User dihapus dari storage (logout)
          setUser(null);
          setIsLoggingOut(false); // Reset logout state
        }
      }
      
      // Juga listen untuk token changes
      if (e.key === "token") {
        if (!e.newValue) {
          // Token dihapus (logout)
          setUser(null);
          setIsLoggingOut(false);
        }
      }
      
      // Listen untuk logout signal dari tab lain
      if (e.key === "logout-signal") {
        console.log("Logout signal detected from another tab");
        setUser(null);
        setIsLoggingOut(false);
        sessionStorage.removeItem("user");
      }
    };

    window.addEventListener("storage", handleStorageChange);
    return () => window.removeEventListener("storage", handleStorageChange);
  }, []);

  // Periodically check auth state sebagai fallback (setiap 5 detik)
  useEffect(() => {
    const interval = setInterval(() => {
      // Hanya check jika tidak ada user di sessionStorage tapi masih ada user state
      const userString = sessionStorage.getItem("user");
      const tokenExists = localStorage.getItem("token");
      
      if (user && !userString && !tokenExists && !isLoggingOut) {
        console.log("Detected auth mismatch, clearing user state");
        setUser(null);
      }
    }, 5000);

    return () => clearInterval(interval);
  }, [user, isLoggingOut]);

  return (
    <AuthContext.Provider value={{ user, isLoading, isLoggingOut, login, logout, checkAuth }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}